import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Company, CompanyType, useAllCompaniesLazyQuery, useBrokersLazyQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, Button, ClickAwayListener, Dialog, Paper, Stack, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import AssetListButtonOption from '../AssetListButtonOption';
import { useFormikContext } from 'formik';
import { PlusCircleOutlined } from '@ant-design/icons';
import { capitalizeFirstLetter } from '../../utils/strings';
import AddCompany from '../merchants/AddCompany';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
interface FormValues {
  company: {
    id?: string;
    logo?: string | null;
    legalName?: string | null;
    type?: string[];
  } | null;
}
export interface Props {
  id: string;
  disabled: boolean;
  companyTypes?: CompanyType[];
}

export const CompaniesAutocomplete = ({ id, disabled, companyTypes }: Props) => {
  const { errors, touched, values, handleBlur, setFieldValue } = useFormikContext<FormValues>();
  const [companies, setCompanies] = useState<readonly Company[]>([]);
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values?.company?.id);
  const inputReference = useRef<HTMLInputElement>(null);
  const [hasInitialFetch, setHasInitialFetch] = useState<boolean>(false);
  const [getAllCompanies] = useAllCompaniesLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const allCompanies = (data.allCompanies.data as Company[]) || [];
      const filteredCompanies = companyTypes?.length
        ? allCompanies.filter(company => company.type?.every(t => companyTypes.includes(t)))
        : allCompanies;
      setCompanies(filteredCompanies);
    }
  });
  const [openDrawerConfirmation, setOpenDrawerConfirmation] = useState<boolean>(false);
  const handleClose = () => {
    setOpenDrawerConfirmation(true);
  };
  const companiesFetch = useMemo(
    () =>
      debounce((input: string) => {
        getAllCompanies({ variables: { search: input, type: companyTypes || [] } });
      }, 200),
    [getAllCompanies, companyTypes]
  );

  const [companyInputValue, setCompanyInputValue] = useState<string>('');
  useEffect(() => {
    setCompanies([]);
    if (companyInputValue) {
      companiesFetch(companyInputValue);
    } else {

      getAllCompanies({
        variables: {
          search: '',
          type: companyTypes || [],
        },
      });
    }
  }, [companyInputValue, companyTypes]);

  const [isCompanyModalShown, showCompanyModal] = useState<boolean>(false);
  const handleAdd = () => {
    showCompanyModal(!isCompanyModalShown);
  };

  const handleInputClick = () => {
    if (!hasInitialFetch) {
      companiesFetch('');
      setHasInitialFetch(true);
    }
  };

  const PaperComponentCustom = (options: any) => {
    const { containerProps, children } = options;
    return (
      <Paper {...containerProps}>
        <Stack>
          {children}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PlusCircleOutlined />}
            onMouseDown={(event) => {
              event.preventDefault();
              handleAdd();
            }}
          >
            Add new {capitalizeFirstLetter(companyTypes?.join('/') || '')} Company
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <Dialog maxWidth="sm" fullWidth onClose={handleClose} open={isCompanyModalShown} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {isCompanyModalShown && (
          <AddCompany
            open={isCompanyModalShown}
            entity={null}
            onCancel={handleClose}
            companyTypes={companyTypes}
            onSave={(company) => {
              setFieldValue('company', company);
              showCompanyModal(false);
            }} />
        )}
      </Dialog>
      {values?.company?.id && (showInfo || disabled) && (
        <Stack direction={'row'}>
          <AssetListButtonOption
            logo={values?.company?.logo || null}
            name={values?.company?.legalName || ''}
            type={`${values?.company?.type?.map(t => capitalizeFirstLetter(t)).join(' / ') || ''} Company`}
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && !disabled && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(disabled ? false : !!values?.company?.id);
          }}
        >
          <Autocomplete
            PaperComponent={PaperComponentCustom}
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Company) => option.legalName || ''}
            options={companies}
            autoComplete
            value={(values.company as Company) || null}
            isOptionEqualToValue={(option: Company, value: Company) => option.id === value.id}
            onChange={(event: SyntheticEvent, newValue: Company | null) => {
              setFieldValue('company', newValue || { id: '' });
              setShowInfo(!!newValue?.id);
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setCompanyInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={`Select ${capitalizeFirstLetter(companyTypes?.join('/') || '')} Company`}
                inputRef={inputReference}
                name="company"
                onClick={handleInputClick}
                onBlur={handleBlur}
                error={Boolean(touched.company?.id && errors.company?.id)}
                helperText={touched.company?.id && errors.company?.id}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.company?.id && (
                    <AssetLogo logo={values?.company?.logo} name={values?.company?.legalName} size={'sm'} />
                  )
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
              return (
                <AssetListButtonOption
                  logo={option.logo || null}
                  name={option.legalName || ''}
                  type={`${option.type?.map(t => capitalizeFirstLetter(t)).join(' / ') || ''} Company`}
                  {...props}
                />
              );
            }}
          />
        </ClickAwayListener>
      )}
      {openDrawerConfirmation && (
        <ActionConfirmation
          title={'The form will close and changes will be lost. Are you sure you want to proceed?'}
          open={openDrawerConfirmation}
          useYesNoButtonLabels={true}
          type="error"
          handleClose={(approved: boolean) => {
            setOpenDrawerConfirmation(false);
            if (!approved) {
              return;
            }
            showCompanyModal(false);
          }}
        />
      )}
    </>
  );
};
